import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Boxes, AlertTriangle, PackageX, DollarSign, Scan, Clock, Package } from "lucide-react";
import { inventoryApi } from "../lib/shop-api";
import { money, dateShort } from "../lib/api";

const Stat = ({ icon: Icon, label, value, accent, testId }) => (
  <div className="bg-[#120F0D] border border-white/10 rounded-xl p-5" data-testid={testId}>
    <div className="flex items-center justify-between mb-2">
      <p className="text-xs uppercase tracking-wider text-[#F5F0E6]/60">{label}</p>
      <Icon size={18} className={accent || "text-[#C89B3C]"} />
    </div>
    <p className="text-3xl font-bold text-[#F5F0E6]" style={{ fontFamily: "'Playfair Display'" }}>{value}</p>
  </div>
);

export default function InventoryDashboard() {
  const [d, setD] = useState(null);

  useEffect(() => {
    inventoryApi.dashboard().then(setD).catch(() => {});
  }, []);

  if (!d) return <p className="text-[#F5F0E6]/60">Cargando…</p>;

  return (
    <div>
      <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-[0.4em] text-[#C89B3C] mb-2">Operaciones</p>
          <h1 className="text-4xl text-[#F5F0E6]" style={{ fontFamily: "'Playfair Display'", fontWeight: 700 }}>Inventario</h1>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/scan" className="flex items-center gap-2 bg-[#C89B3C] hover:bg-[#E6BF5F] text-[#0A0908] font-bold uppercase tracking-wider px-5 py-3 rounded-full" data-testid="inv-scan-btn">
            <Scan size={18} /> SCAN PRODUCT
          </Link>
          <Link to="/admin/products/new" className="flex items-center gap-2 bg-white/5 border border-white/15 text-[#F5F0E6] px-4 py-3 rounded-full text-sm hover:border-[#C89B3C]">
            + Producto
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat icon={Package} label="Productos" value={d.total_products} testId="stat-products" />
        <Stat icon={Boxes} label="Unidades en stock" value={d.total_units} testId="stat-units" />
        <Stat icon={AlertTriangle} label="Low stock" value={d.low_stock_count} accent="text-amber-400" testId="stat-low" />
        <Stat icon={PackageX} label="Agotados" value={d.out_of_stock_count} accent="text-red-400" testId="stat-out" />
      </div>

      <div className="bg-[#120F0D] border border-[#C89B3C]/30 rounded-xl p-5 mb-6">
        <div className="flex items-center gap-3">
          <DollarSign className="text-[#C89B3C]" size={24} />
          <div>
            <p className="text-xs uppercase tracking-wider text-[#F5F0E6]/60">Valor del inventario</p>
            <p className="text-4xl font-bold text-[#C89B3C]" style={{ fontFamily: "'Playfair Display'" }} data-testid="stat-value">{money(d.inventory_value)}</p>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <section className="bg-[#120F0D] border border-white/10 rounded-xl p-5">
          <h2 className="text-lg text-[#F5F0E6] mb-3 flex items-center gap-2" style={{ fontFamily: "'Playfair Display'" }}>
            <AlertTriangle size={18} className="text-amber-400" /> Low stock
          </h2>
          {d.low_stock.length === 0 ? <p className="text-sm text-[#F5F0E6]/50">Todo bien.</p> : (
            <ul className="space-y-2" data-testid="low-stock-list">
              {d.low_stock.map((p) => (
                <li key={p.id}>
                  <Link to={`/admin/products/${p.id}`} className="flex justify-between py-1.5 border-b border-white/5 hover:text-[#C89B3C]">
                    <span className="text-sm text-[#F5F0E6]/90 truncate pr-2">{p.name}</span>
                    <span className="text-sm font-bold text-amber-400">{p.total_stock}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="bg-[#120F0D] border border-white/10 rounded-xl p-5">
          <h2 className="text-lg text-[#F5F0E6] mb-3 flex items-center gap-2" style={{ fontFamily: "'Playfair Display'" }}>
            <PackageX size={18} className="text-red-400" /> Agotados
          </h2>
          {d.out_of_stock.length === 0 ? <p className="text-sm text-[#F5F0E6]/50">Ninguno.</p> : (
            <ul className="space-y-2" data-testid="out-of-stock-list">
              {d.out_of_stock.map((p) => (
                <li key={p.id}>
                  <Link to={`/admin/products/${p.id}`} className="flex justify-between py-1.5 border-b border-white/5 hover:text-[#C89B3C]">
                    <span className="text-sm text-[#F5F0E6]/90 truncate pr-2">{p.name}</span>
                    <span className="text-sm font-bold text-red-400">0</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <section className="bg-[#120F0D] border border-white/10 rounded-xl p-5">
          <h2 className="text-lg text-[#F5F0E6] mb-3 flex items-center gap-2" style={{ fontFamily: "'Playfair Display'" }}>
            <Scan size={18} /> Recién escaneados
          </h2>
          {d.recent_scans.length === 0 ? <p className="text-sm text-[#F5F0E6]/50">Nada aún.</p> : (
            <ul className="space-y-2 text-sm" data-testid="recent-scans-list">
              {d.recent_scans.map((s) => (
                <li key={s.id} className="flex justify-between py-1.5 border-b border-white/5">
                  <span className="text-[#F5F0E6]/90 truncate pr-2">{s.product_name}</span>
                  <span className="text-xs text-[#F5F0E6]/50">{dateShort(s.timestamp)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="bg-[#120F0D] border border-white/10 rounded-xl p-5">
          <h2 className="text-lg text-[#F5F0E6] mb-3 flex items-center gap-2" style={{ fontFamily: "'Playfair Display'" }}>
            <Clock size={18} /> Agregados recientemente
          </h2>
          {d.recent_added.length === 0 ? <p className="text-sm text-[#F5F0E6]/50">Nada aún.</p> : (
            <ul className="space-y-2 text-sm" data-testid="recent-added-list">
              {d.recent_added.map((p) => (
                <li key={p.id} className="flex justify-between py-1.5 border-b border-white/5">
                  <Link to={`/admin/products/${p.id}`} className="text-[#F5F0E6]/90 hover:text-[#C89B3C] truncate pr-2">{p.name}</Link>
                  <span className="text-xs text-[#F5F0E6]/50">{dateShort(p.created_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <section className="bg-[#120F0D] border border-white/10 rounded-xl p-5">
        <h2 className="text-lg text-[#F5F0E6] mb-3" style={{ fontFamily: "'Playfair Display'" }}>Por categoría</h2>
        <table className="w-full text-sm" data-testid="by-category-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-widest text-[#F5F0E6]/50 border-b border-white/10">
              <th className="py-2">Categoría</th>
              <th>Productos</th>
              <th>Unidades</th>
              <th className="text-right">Valor</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(d.by_category).sort((a,b) => b[1].value - a[1].value).map(([cat, s]) => (
              <tr key={cat} className="border-b border-white/5">
                <td className="py-2 text-[#F5F0E6]/90">{cat}</td>
                <td className="text-[#F5F0E6]/80">{s.products}</td>
                <td className="text-[#F5F0E6]/80">{s.units}</td>
                <td className="text-right text-[#C89B3C]">{money(s.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
